import {
    ERROR_DESC_CANNOT_SEND_GROUP_MSG,
    ERROR_TITLE_SORRY
} from "@/constants/errors";
import { SendHorizonal } from "lucide-react";
import {
  selectConversations,
  selectCurrConversation,
  selectKeyExchangeVersion,
  selectKeyExchangeVersions,
  keyVersionForAddress,
  selectEncryptionEnabled,
  selectGroupEncryptionEnabled,
  setConversationEncryptionReady,
} from "@/features/control/controlSlice";
import {
  selectHasUndecryptedMessages,
  selectMessagesLoading,
  selectSending,
  sendMessage,
  setReloadMessages,
} from "@/features/chat/chatSlice";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CHAT_MSG_TEXTAREA_LOADING_PLACEHOLDER,
  CHAT_MSG_TEXTAREA_PLACEHOLDER,
} from "@/constants/ui";
import { E2EE_SEND_ENABLED, E2EE_GROUP_ENABLED } from "@/constants/e2ee";
import { Conversation } from "@/types/conversation";
import FileUploadDialog from "./FileUploadDialog";
import { Textarea } from "@/components/ui/textarea";
import { WEBMSG_API_GROUP_MSG_SEND_ERROR } from "../../constants/errors";
import { useToast } from "@/components/ui/use-toast";
import { useEncryption } from "@/hooks/useEncryption";
import { ensureFreshPublicKey, requestPublicKey } from "@/services/keyExchangeService";
import { getGroups } from "@/services/userGroupService";
import { sendGroupKeyRequest } from "@/services/groupKeyService";

const GROUP_KEY_REQUEST_COOLDOWN_MS = 5 * 60 * 1000;

const ChatInputs = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const isSending = useAppSelector(selectSending);
  const messagesLoading = useAppSelector(selectMessagesLoading);
  const userId = useAppSelector((state) => state.auth.userId);
  const username = useAppSelector((state) => state.auth.username);
  const keyExchangeVersion = useAppSelector(selectKeyExchangeVersion);
  // Per-address key versions (the global counter re-runs checkEncryption; these gate the
  // reload). In a ref so the post-await gate reads the value current at decision time.
  const keyExchangeVersions = useAppSelector(selectKeyExchangeVersions);
  const keyExchangeVersionsRef = useRef(keyExchangeVersions);
  keyExchangeVersionsRef.current = keyExchangeVersions;
  const encryptionEnabled = useAppSelector(selectEncryptionEnabled);
  const groupEncryptionEnabled = useAppSelector(selectGroupEncryptionEnabled);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const [message, setMessage] = useState<string>("");
  const [canEncrypt, setCanEncrypt] = useState<boolean>(false);
  // Shown when an encrypted group send is attempted without a usable symmetric key —
  // we request the key from the owner and block the send rather than leaking plaintext.
  const [showMissingKeyDialog, setShowMissingKeyDialog] = useState<boolean>(false);
  const { encrypt, encryptForGroup, canEncryptFor, canEncryptGroup, setupGroupEncryption, status } = useEncryption();
  // Mutable ref so async functions inside checkEncryption can read the current
  // isInitialized value without being stuck on a stale closure capture.
  // Updated on every render (safe because refs are mutable outside effects).
  const isInitializedRef = useRef(status.isInitialized);
  isInitializedRef.current = status.isInitialized;
  // Whether the current view still holds any 🔒 message. Read inside the async
  // checkEncryption to decide if a reload is worthwhile — a fully-decrypted view
  // (the common cached case) needs no reload, avoiding a full re-fetch + attachment
  // re-pull. Kept in a ref so the post-await decision reads the current value.
  const hasUndecrypted = useAppSelector(selectHasUndecryptedMessages);
  const hasUndecryptedRef = useRef(hasUndecrypted);
  hasUndecryptedRef.current = hasUndecrypted;
  // Whether the current conversation is a group — read in the keyExchangeVersion
  // effect without adding it to the dep array (avoids extra effect runs).
  const isGroupConversationRef = useRef(false);
  const currConv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
  isGroupConversationRef.current = currConv?.type === "Group";
  // Sending is blocked until the history loads: a send mid-load errors out, and its optimistic
  // row is wiped by the arriving fetch. No enabled gap on a switch (loadMessages is dispatched
  // in the same tick the pane clears); cache hits and background refreshes leave `loading`
  // false, so neither blocks.
  const isConversationLoading = currConv == null || messagesLoading;
  // Tracks the last address for which we attempted a key fetch.
  const lastKeyFetchAddressRef = useRef<string | null>(null);
  // Tracks the last address for which the 1:1 key fetch SUCCEEDED. Used to break
  // the infinite-fetch loop: requestPublicKey → @keys WebSocket response →
  // keyExchangeReceived() → keyExchangeVersion++ → lastKeyFetchAddressRef reset
  // → requestPublicKey again → … The reset must be suppressed once we have the key.
  // NOTE: intentionally NOT set for group conversations — group owners should
  // re-run setupGroupEncryption on each keyExchangeVersion bump so that newly
  // arrived member keys immediately trigger key distribution.
  const keyFetchedForAddressRef = useRef<string | null>(null);
  // Tracks the address currently being fetched. Prevents the keyExchangeVersion
  // reset effect from clearing lastKeyFetchAddressRef while a fetch is in-flight
  // (own sendkeys WebSocket echo increments keyExchangeVersion before the HTTP
  // response resolves, which would otherwise trigger a second fetch).
  const keyFetchInFlightRef = useRef<string | null>(null);
  // Tracks when a key fetch last failed per address. Prevents repeated retries
  // (e.g. timeout spam) when the backend is unavailable.
  const keyFetchFailedAtRef = useRef<Map<string, number>>(new Map());
  // Per-address key version at which each conversation last had its key-ready reload —
  // a Map (not a single last-address ref) so A↔B alternation doesn't re-trigger reloads.
  const reloadedAtVersionRef = useRef<Map<string, number>>(new Map());
  // Tracks the last group address for which we sent a group_key_request so we
  // don't spam the owner on every keyExchangeVersion increment.
  const lastKeyRequestAddressRef = useRef<string | null>(null);
  // Tracks when each group key request was last sent so we can retry after the
  // cooldown if the distribution never arrived (e.g. owner was offline).
  const keyRequestSentAtRef = useRef<Map<string, number>>(new Map());
  // Tracks when setupGroupEncryption last completed per group address. Used to
  // absorb distribution echoes: each distributed-to member triggers a
  // keyExchangeVersion bump, which would otherwise immediately re-trigger setup.
  const groupSetupLastRunAtRef = useRef<Map<string, number>>(new Map());

  // Reset the attempt guard when a key exchange event fires (e.g. peer just shared
  // their key), but ONLY if no fetch is in-flight and the address hasn't already
  // been successfully fetched. Without this guard, our own requestPublicKey call
  // triggers a WebSocket echo → keyExchangeReceived → resets the ref →
  // requestPublicKey again → loop.
  // For group conversations the "fetch succeeded" guard is intentionally skipped so
  // that setupGroupEncryption re-runs whenever a member's key exchange event fires —
  // this ensures newly arrived member keys immediately receive their key distribution.
  const KEY_FETCH_RETRY_COOLDOWN_MS = 60_000;
  // Long enough to absorb all per-member distribution echoes (each bump arrives
  // within a few seconds of each other), short enough that a member who comes
  // online later will still trigger a re-run.
  const GROUP_SETUP_ECHO_COOLDOWN_MS = 30_000;
  useEffect(() => {
    const addr = lastKeyFetchAddressRef.current;
    const failedAt = addr ? (keyFetchFailedAtRef.current.get(addr) ?? 0) : 0;
    const lastRunAt = isGroupConversationRef.current && addr
      ? (groupSetupLastRunAtRef.current.get(addr) ?? 0)
      : 0;
    const blocked =
      addr !== null &&
      (keyFetchInFlightRef.current === addr ||   // fetch in-flight
        (!isGroupConversationRef.current && keyFetchedForAddressRef.current === addr) || // 1:1 only: suppress loop after success
        Date.now() - failedAt < KEY_FETCH_RETRY_COOLDOWN_MS || // failed recently
        Date.now() - lastRunAt < GROUP_SETUP_ECHO_COOLDOWN_MS); // group setup ran recently — absorb echoes
    if (!blocked) {
      lastKeyFetchAddressRef.current = null;
    }
  }, [keyExchangeVersion]);

  // Check if encryption is available for the current conversation.
  // For 1:1: fetch the recipient's public key from @keys if not cached.
  //   The key fetch starts as soon as userId is available — it does NOT wait for
  //   status.isInitialized. The HTTP round-trip to @keys doesn't need the own key,
  //   so fetching early means the peer key is in IndexedDB by the time own-key init
  //   completes and the ChatMessages reload fires.
  // For groups: if current user is the owner, generate and distribute the key.
  useEffect(() => {
    const checkEncryption = async () => {
      if (currConversationIndex === null || !userId) {
        setCanEncrypt(false);
        return;
      }

      const conversation = conversations[currConversationIndex];
      if (!conversation) {
        setCanEncrypt(false);
        return;
      }

      if (conversation.type === "Group") {
        // Skip the getGroups()/setup work; the sends underneath are guarded too.
        if (!E2EE_GROUP_ENABLED) {
          setCanEncrypt(false);
          dispatch(setConversationEncryptionReady(false));
          return;
        }

        const groupAddress = conversation.address;
        const groupName = conversation.groupName || "";

        // If the current user is the group owner, generate + distribute the key.
        // If a non-owner, send a group_key_request to the owner if we lack the key.
        // ownerName may be absent after page reload (GetConversations doesn't return it),
        // so fall back to the ownerName from the groups list which always has it.
        if (username && lastKeyFetchAddressRef.current !== groupAddress) {
          try {
            const groups = await getGroups();
            const group = groups.find((g) => g.address === groupAddress || g.name === groupName);
            const ownerName = conversation.ownerName || group?.ownerName || "";
            if (group && username === ownerName) {
              // Owner: generate + distribute the key. Distribution needs our own EC keypair
              // (distributeGroupKeyToMembers no-ops without it), so defer until init. Don't
              // set lastKeyFetchAddressRef while deferring — leaving it unset lets this effect
              // (dep: status.isInitialized) re-run and distribute once the keypair is ready.
              if (isInitializedRef.current) {
                // keyFetchInFlightRef blocks the keyExchangeVersion effect from
                // resetting lastKeyFetchAddressRef while distribution is running
                // (each distributed-to member echo would otherwise re-trigger setup).
                // groupSetupLastRunAtRef absorbs echoes that arrive after the await
                // resolves (before keyFetchInFlightRef is cleared they're blocked too).
                lastKeyFetchAddressRef.current = groupAddress;
                keyFetchInFlightRef.current = groupAddress;
                try {
                  await setupGroupEncryption(groupAddress, groupName, ownerName, group.members);
                  groupSetupLastRunAtRef.current.set(groupAddress, Date.now());
                } finally {
                  keyFetchInFlightRef.current = null;
                }
              }
            } else if (group && ownerName && userId) {
              // Non-owner: request the key from the owner if we don't have it yet.
              // Matches Android's DefaultGroupKeyExchangeManager.getAllSymmetricKeyRequests().
              const hasKey = await canEncryptGroup(groupAddress);
              if (!hasKey) {
                const lastSentAt = keyRequestSentAtRef.current.get(groupAddress) ?? 0;
                const cooldownExpired = Date.now() - lastSentAt > GROUP_KEY_REQUEST_COOLDOWN_MS;
                if (lastKeyRequestAddressRef.current !== groupAddress || cooldownExpired) {
                  lastKeyRequestAddressRef.current = groupAddress;
                  keyRequestSentAtRef.current.set(groupAddress, Date.now());
                  lastKeyFetchAddressRef.current = groupAddress;
                  // Pre-fetch the owner's ECDH public key in parallel with the key request.
                  // The distribution message is encrypted for us using the owner's private key,
                  // so we need the owner's public key in IDB before their response arrives.
                  const ensureOwnerKey = canEncryptFor(ownerName).then(async (has) => {
                    if (!has) await requestPublicKey(ownerName, Number(userId));
                  }).catch(() => {});
                  await Promise.all([
                    ensureOwnerKey,
                    sendGroupKeyRequest(ownerName, group.id, Number(userId)).catch((err) =>
                      console.error("[GroupKey] Failed to send group key request:", err)
                    ),
                  ]);
                }
              }
            }
          } catch (err) {
            console.error("[GroupKey] Failed to set up group encryption:", err);
          }
        }

        const hasKey = await canEncryptGroup(groupAddress);
        // Gate on init like the 1:1 path: encryptForGroup throws without our own keypair, so
        // advertising "encrypted" before init would let a send fall back to PLAINTEXT. The
        // effect re-runs when isInitialized flips. (Reload below only needs the key.)
        const canEncryptNow = hasKey && isInitializedRef.current;
        setCanEncrypt(canEncryptNow);
        dispatch(setConversationEncryptionReady(canEncryptNow));

        // Reload once the group key arrives so existing 🔒 rows decrypt — only while the
        // view still has retryable rows and THIS group's key version moved. Mirrors 1:1 below.
        const groupKeyVersion = keyVersionForAddress(keyExchangeVersionsRef.current, groupAddress);
        if (
          hasKey &&
          hasUndecryptedRef.current &&
          reloadedAtVersionRef.current.get(groupAddress) !== groupKeyVersion
        ) {
          reloadedAtVersionRef.current.set(groupAddress, groupKeyVersion);
          dispatch(setReloadMessages(true));
        }
        return;
      }

      // 1:1 conversation — fetch peer key without waiting for own-key init.
      const recipientUsername = conversation.address;
      let hasKey = await canEncryptFor(recipientUsername);

      if (lastKeyFetchAddressRef.current !== conversation.address) {
        lastKeyFetchAddressRef.current = conversation.address;
        keyFetchInFlightRef.current = conversation.address;
        try {
          // TTL-gated (5 min): a no-op on rapid re-navigation when the peer key is
          // already fresh, so re-opening a conversation doesn't re-send getkeys. Still
          // fetches when the key is missing or stale (catches peer re-logins).
          await ensureFreshPublicKey(conversation.address, Number(userId));
          hasKey = await canEncryptFor(recipientUsername);
          if (hasKey) {
            // Mark as successfully fetched so the keyExchangeVersion reset effect
            // won't re-trigger a fetch loop on the WebSocket echo from @keys.
            keyFetchedForAddressRef.current = conversation.address;
            keyFetchFailedAtRef.current.delete(conversation.address);
          }
        } catch (err) {
          console.error("[E2EE] Could not fetch public key for", conversation.address, err);
          keyFetchFailedAtRef.current.set(conversation.address, Date.now());
        } finally {
          keyFetchInFlightRef.current = null;
        }
      }

      // Use the ref here (not the closure-captured status.isInitialized) so that
      // if isInitialized flips while requestPublicKey was in-flight we still read
      // the current value rather than the stale one from when the effect started.
      const isReady = hasKey && isInitializedRef.current;

      // Reload once both keys are ready — only while the view still has retryable 🔒 rows
      // and THIS peer's key version moved (otherwise it re-runs the same failed decrypts
      // and re-pulls every attachment).
      const peerKeyVersion = keyVersionForAddress(keyExchangeVersionsRef.current, conversation.address);
      if (
        isReady &&
        hasUndecryptedRef.current &&
        reloadedAtVersionRef.current.get(conversation.address) !== peerKeyVersion
      ) {
        reloadedAtVersionRef.current.set(conversation.address, peerKeyVersion);
        dispatch(setReloadMessages(true));
      }

      // Outbound encryption requires both peer key and own key to be ready.
      setCanEncrypt(isReady);
    };

    checkEncryption();
  }, [currConversationIndex, conversations, canEncryptFor, canEncryptGroup, setupGroupEncryption, status.isInitialized, userId, username, keyExchangeVersion]);

  // Requests the group symmetric key from the owner (cooldown-gated, shared with the on-open
  // path via keyRequestSentAtRef). Used by the send-time guard so a member without the key
  // gets it on the owner's next response instead of the message going out in plaintext.
  const requestGroupKeyFromOwner = async (conversation: Conversation) => {
    if (!userId || !username) return;
    const groupAddress = conversation.address;
    const lastSentAt = keyRequestSentAtRef.current.get(groupAddress) ?? 0;
    if (Date.now() - lastSentAt <= GROUP_KEY_REQUEST_COOLDOWN_MS) return;
    try {
      const groups = await getGroups();
      const group = groups.find(
        (g) => g.address === groupAddress || g.name === (conversation.groupName || "")
      );
      const ownerName = conversation.ownerName || group?.ownerName || "";
      if (!group || !ownerName || ownerName === username) return;
      keyRequestSentAtRef.current.set(groupAddress, Date.now());
      // We decrypt the owner's response with their ECDH public key — make sure it's cached.
      const hasOwnerKey = await canEncryptFor(ownerName);
      if (!hasOwnerKey) await requestPublicKey(ownerName, Number(userId)).catch(() => {});
      await sendGroupKeyRequest(ownerName, group.id, Number(userId));
    } catch (err) {
      console.error("[GroupKey] send-time key request failed:", err);
    }
  };

  // Helper function for sending a message through the file upload dialog
  const handleSendTextMessage = async (forcePlaintext = false) => {
    // The loading guard also covers "Send anyway": a reload can start while that dialog is open.
    if (message.trim().length === 0 || currConversationIndex === null || isConversationLoading) {
      return;
    }

    const conversation = conversations[currConversationIndex];
    const { addressId, address, addressType } = conversation;
    const isGroup = conversation.type === "Group";

    // Group send-time guard: canEncrypt state can lag the real key, so re-check on send
    // (one IDB read). If E2EE is wanted for this group but we lack a usable key, warn the user
    // (draft preserved) so they can cancel or send unencrypted. forcePlaintext skips the guard —
    // the user already chose "send anyway".
    if (!forcePlaintext && isGroup && E2EE_SEND_ENABLED && E2EE_GROUP_ENABLED && routerE2eeEnabled && groupEncryptionEnabled) {
      const groupReady = (await canEncryptGroup(address)) && status.isInitialized;
      if (!groupReady) {
        void requestGroupKeyFromOwner(conversation);
        setShowMissingKeyDialog(true);
        return;
      }
    }

    let messageToSend = message;
    // Groups respect groupEncryptionEnabled toggle; 1:1 messages respect encryptionEnabled.
    // forcePlaintext (user chose "send anyway") sends without encryption.
    // E2EE_SEND_ENABLED off forces plaintext; the decrypt path is unaffected.
    const shouldEncrypt = !forcePlaintext && E2EE_SEND_ENABLED && routerE2eeEnabled &&
      (isGroup ? E2EE_GROUP_ENABLED && groupEncryptionEnabled : canEncrypt && encryptionEnabled);

    let encrypted = false;
    if (shouldEncrypt) {
      try {
        if (isGroup) {
          messageToSend = await encryptForGroup(message, address, conversation.groupName || "");
        } else {
          messageToSend = await encrypt(message, address);
        }
        encrypted = true;
      } catch (error) {
        if (isGroup) {
          // Encryption unexpectedly failed — warn (draft preserved) so the user can cancel or
          // choose to send unencrypted, rather than silently leaking plaintext.
          console.error("Group encryption failed unexpectedly:", error);
          void requestGroupKeyFromOwner(conversation);
          setShowMissingKeyDialog(true);
          return;
        }
        console.error("Encryption failed, sending as plaintext:", error);
        messageToSend = message;
        toast({
          variant: "destructive",
          title: "Encryption Failed",
          description: "Could not encrypt message — sending as plaintext.",
        });
      }
    }

    const msgResponse = await dispatch(
      sendMessage({
        address,
        addressId,
        addressType,
        message: messageToSend,
        displayMessage: encrypted ? message : undefined,
      })
    );
    

    // TODO: potentially show toast for other send errors
    // Show toast if there is an error response (for groups)
    if (sendMessage.rejected.match(msgResponse)) {
      const errorMessage = msgResponse.error.message;
      console.error(errorMessage);

      if (errorMessage === WEBMSG_API_GROUP_MSG_SEND_ERROR) {
        return toast({
          variant: "destructive",
          title: ERROR_TITLE_SORRY,
          description: ERROR_DESC_CANNOT_SEND_GROUP_MSG,
        });
      }
    }
  };

  useEffect(() => {
    if (isSending) {
      setMessage("");
    }
  }, [isSending]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendTextMessage();
    }
  };

  return (
    <div className="flex w-full gap-2 mt-2">

      <FileUploadDialog disabled={isConversationLoading} />
      <Textarea
        placeholder={
          isConversationLoading
            ? CHAT_MSG_TEXTAREA_LOADING_PLACEHOLDER
            : CHAT_MSG_TEXTAREA_PLACEHOLDER
        }
        className="resize-none"
        rows={1}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        value={message}
        disabled={isConversationLoading}
      />

      <Button
        disabled={isConversationLoading || message.trim().length === 0}
        onClick={() => handleSendTextMessage()}
      >
        <SendHorizonal size={20} />
      </Button>

      <AlertDialog open={showMissingKeyDialog} onOpenChange={setShowMissingKeyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encryption key not ready</AlertDialogTitle>
            <AlertDialogDescription>
              We don't have this group's encryption key from the owner yet, so this message
              can only be sent unencrypted. Cancel to keep your draft and try again once the
              key arrives, or send it unencrypted now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowMissingKeyDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowMissingKeyDialog(false);
                void handleSendTextMessage(true);
              }}
            >
              Send anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChatInputs;
